import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Repeat } from "lucide-react";
import { SocialFeedItem } from "./SocialFeedItem";
import { connectionProfile, primePlayable } from "./mediaCache";
import { setCurrentSocialVideo } from "@/lib/social/currentVideo";
import type { SocialAccount, SocialPost } from "@/services/socialFeedService";
import { useT } from "@/i18n/useLocale";

interface Props {
  posts: SocialPost[];
  accounts: SocialAccount[];
  likedIds: Set<string>;
  likeCounts: Map<string, number>;
  onToggleLike: (postId: string) => void;
}

export function SocialFeed({ posts, accounts, likedIds, likeCounts, onToggleLike }: Props) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const [height, setHeight] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);

  // Fill whatever space is left below the header.
  useEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Phones keep a fixed bottom menu, so leave room for it.
      const bottomBar = window.innerWidth < 768 ? 64 : 0;
      setHeight(Math.max(320, window.innerHeight - top - bottomBar));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Resolve the opening batch as soon as the feed appears. Phones and slow
  // links start with fewer so the first video is not queued behind the rest.
  useEffect(() => {
    const opening = connectionProfile.small ? 3 : 8;
    void primePlayable(posts.slice(0, opening).map((p) => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  // Keep resolving ahead of the viewer.
  useEffect(() => {
    void primePlayable(posts.slice(activeIndex + 1, activeIndex + 5).map((p) => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Which post is on screen right now.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const index = Math.round(el.scrollTop / Math.max(1, el.clientHeight));
        setActiveIndex(Math.min(posts.length - 1, Math.max(0, index)));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
    };
  }, [posts.length]);

  // Publish the video on screen so AI tools can answer "what am I watching?".
  useEffect(() => {
    const post = posts[activeIndex];
    if (!post) {
      setCurrentSocialVideo(null);
      return;
    }
    const account = accountById.get(post.account_id);
    setCurrentSocialVideo({
      id: post.id,
      caption: post.caption,
      creator: account?.display_name ?? account?.handle ?? null,
      creatorHandle: account?.handle ?? null,
      platform: account?.platform ?? null,
      postedAt: post.posted_at,
      originalUrl: post.post_url,
      position: activeIndex + 1,
      total: posts.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, posts]);

  useEffect(() => () => setCurrentSocialVideo(null), []);

  const step = useCallback((direction: 1 | -1) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ top: direction * el.clientHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === "j") {
        e.preventDefault();
        step(1);
      }
      if (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "k") {
        e.preventDefault();
        step(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  return (
    <div className="relative bg-black">
      <div
        ref={containerRef}
        style={{ height: height ? `${height}px` : "100dvh" }}
        className="snap-y snap-mandatory overflow-y-auto overscroll-contain bg-black"
      >
        {posts.map((post, index) => {
          // Only posts near the current one stay mounted; the rest are empty
          // snap slots so scrolling and position stay exactly the same.
          const near = index >= activeIndex - 2 && index <= activeIndex + 3;
          if (!near) {
            return (
              <div key={post.id} className="h-full w-full snap-start snap-always bg-black" />
            );
          }
          return (
            <SocialFeedItem
              key={post.id}
              post={post}
              account={accountById.get(post.account_id)}
              active={index === activeIndex}
              warm={index > activeIndex && index <= activeIndex + 2}
              autoScroll={autoScroll}
              liked={likedIds.has(post.id)}
              likeCount={likeCounts.get(post.id) ?? 0}
              onToggleLike={() => onToggleLike(post.id)}
              onEnded={() => step(1)}
            />
          );
        })}
      </div>

      <div className="pointer-events-none absolute right-4 top-1/2 hidden -translate-y-1/2 flex-col gap-3 md:flex lg:right-[calc(380px+1.5rem)]">
        <button
          type="button"
          aria-label={t("auto.previous-post-bc9d")}
          onClick={() => step(-1)}
          className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label={t("auto.next-post-ebd8")}
          onClick={() => step(1)}
          className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-pressed={autoScroll}
          onClick={() => setAutoScroll((v) => !v)}
          title={t("auto.play-the-next-video-automatically-7ca8")}
          className={`pointer-events-auto flex h-11 w-11 flex-col items-center justify-center rounded-full text-[10px] font-semibold uppercase backdrop-blur transition ${
            autoScroll ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/20"
          }`}
        >
          <Repeat className="h-4 w-4" />
          {autoScroll ? "On" : "Off"}
        </button>
      </div>
    </div>
  );
}
